import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Wallet, AlertTriangle, CheckCircle2, Plus } from "lucide-react";

/**
 * Credit card displayed in the Partner area (Dashboard, Orders, Checkout).
 * Props:
 *   - compact: boolean - renders a horizontal, condensed layout
 *   - credit:  optional { credit_initial, credit_used, credit_balance } to avoid fetch
 *   - partnerId: optional id (admin usage)
 */
export default function PartnerCreditCard({ compact = false, credit: creditProp, partnerId, showRecharge = false }) {
  const [credit, setCredit] = useState(creditProp || null);
  useEffect(() => {
    if (creditProp) { setCredit(creditProp); return; }
    const url = partnerId ? `/partners/${partnerId}/credit` : "/partners/me/credit";
    // Fallback: use the explicit me endpoint via auth.me partner id if no explicit partnerId
    // Here we expect caller to pass credit or partnerId; otherwise do nothing.
    if (!partnerId) return;
    api.get(url).then((r) => setCredit(r.data)).catch(() => {});
  }, [creditProp, partnerId]);

  if (!credit) {
    return (
      <Card className="border-slate-200" data-testid="partner-credit-card-loading">
        <CardContent className="p-5 text-slate-400 text-sm">Caricamento credito…</CardContent>
      </Card>
    );
  }
  const initial = Number(credit.credit_initial || 0);
  const used = Number(credit.credit_used || 0);
  const balance = Number(credit.credit_balance || 0);
  const pct = initial > 0 ? Math.max(0, Math.min(100, (balance / initial) * 100)) : 0;
  const exhausted = initial > 0 && balance <= 0;
  const low = !exhausted && initial > 0 && pct < 20;
  const tone = exhausted ? "red" : low ? "amber" : balance > 0 ? "emerald" : "slate";
  const toneBg = {
    red: "bg-red-50 border-red-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    slate: "bg-slate-50 border-slate-200",
  }[tone];
  const toneBar = {
    red: "bg-red-500",
    amber: "bg-amber-500",
    emerald: "bg-emerald-500",
    slate: "bg-slate-300",
  }[tone];
  const toneLabel = {
    red: "text-red-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    slate: "text-slate-600",
  }[tone];

  const Icon = exhausted ? AlertTriangle : low ? AlertTriangle : CheckCircle2;

  // Variante minima: mostra SOLO il residuo (richiesta Dashboard Partner · Credito Partner).
  if (compact === "minimal") {
    return (
      <Card className={`border ${toneBg}`} data-testid="partner-credit-card">
        <CardContent className="p-5 flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl bg-white/70 border ${toneBg} flex items-center justify-center`}>
            <Wallet className={`w-6 h-6 ${toneLabel}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Credito Partner Disponibile</div>
            <div className={`font-display font-extrabold text-3xl ${toneLabel}`} data-testid="credit-balance">
              {eur(balance)}
            </div>
            {initial > 0 && (
              <div className="mt-2 h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                <div className={`h-full ${toneBar}`} style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
          {showRecharge && (
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid="dashboard-recharge-button">
              <Link to="/partner/ricarica"><Plus className="w-4 h-4 mr-1" /> Ricarica</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border ${toneBg}`} data-testid="partner-credit-card">
      <CardContent className={compact ? "p-4" : "p-5"}>
        <div className={compact ? "flex items-center gap-6 flex-wrap" : "space-y-3"}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-white/70 border ${toneBg} flex items-center justify-center`}>
              <Wallet className={`w-5 h-5 ${toneLabel}`} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Credito Partner</div>
              <div className={`font-display font-extrabold text-2xl ${toneLabel}`} data-testid="credit-balance">
                {eur(balance)}
              </div>
            </div>
          </div>
          <div className={compact ? "flex items-center gap-5 text-sm" : "grid grid-cols-2 gap-3 text-sm pt-2"}>
            <div>
              <div className="text-xs uppercase text-slate-500 font-semibold">Utilizzato</div>
              <div className="font-semibold text-slate-700" data-testid="credit-used">{eur(used)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500 font-semibold">Assegnato</div>
              <div className="font-semibold text-slate-700" data-testid="credit-initial">{eur(initial)}</div>
            </div>
          </div>
        </div>
        {initial > 0 && (
          <div className="mt-3">
            <div className="h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
              <div className={`h-full ${toneBar}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-xs">
              <span className={`inline-flex items-center gap-1 ${toneLabel} font-semibold`}>
                <Icon className="w-3.5 h-3.5" />
                {exhausted ? "Il tuo Credito Partner è esaurito."
                  : low ? "Credito in esaurimento (sotto 20%)."
                  : "Credito disponibile."}
              </span>
              <span className="text-slate-400">{pct.toFixed(0)}% residuo</span>
            </div>
          </div>
        )}
        {initial <= 0 && (
          <div className="mt-2 text-xs text-slate-500">Nessun credito assegnato. Contatta Centro Studi Athena.</div>
        )}
      </CardContent>
    </Card>
  );
}
